import { Logger } from '../core/Logger';

export interface LoadingTimeline {
    url: string;
    type: 'texture' | 'video';
    timestamps: {
        registered: number;
        loadStart: number;
        firstProgress?: number;
        lastProgress?: number;
        completed: number;
        error?: number;
    };
    progressEvents: Array<{
        timestamp: number;
        loaded: number;
        total: number;
        percent: number;
    }>;
    fileSize?: number;
    dimensions?: { width: number; height: number };
    totalTime: number;
    phases: {
        registration: number;
        actualLoading: number;
        processing: number;
    };
}

/**
 * MaterialLoadingDebugger - Detaillierte Performance-Analyse für Material Loading
 */
export class MaterialLoadingDebugger {
    private static instance: MaterialLoadingDebugger;
    private timelines: Map<string, LoadingTimeline> = new Map();
    private globalStartTime: number = 0;
    private isEnabled: boolean = true;

    public static get Instance(): MaterialLoadingDebugger {
        if (!MaterialLoadingDebugger.instance) {
            MaterialLoadingDebugger.instance = new MaterialLoadingDebugger();
        }
        return MaterialLoadingDebugger.instance;
    }

    /**
     * Material für Tracking registrieren
     */
    public registerMaterial(url: string, type: 'texture' | 'video'): void {
        if (!this.isEnabled) return;
        
        const now = performance.now();
        if (this.globalStartTime === 0) {
            this.globalStartTime = now;
        }

        this.timelines.set(url, {
            url,
            type,
            timestamps: {
                registered: now,
                loadStart: 0,
                completed: 0
            },
            progressEvents: [],
            totalTime: 0,
            phases: {
                registration: 0,
                actualLoading: 0,
                processing: 0
            }
        });

        //Logger.Instance.Debug(`📊 [DEBUG] Registered ${type}: ${url} at ${(now - this.globalStartTime).toFixed(2)}ms`, 'MaterialDebug');
    }

    /**
     * Laden gestartet
     */
    public markLoadStart(url: string): void {
        if (!this.isEnabled) return;
        
        const timeline = this.timelines.get(url);
        if (timeline) {
            const now = performance.now();
            timeline.timestamps.loadStart = now;
            timeline.phases.registration = now - timeline.timestamps.registered;
            
            //Logger.Instance.Debug(`🚀 [DEBUG] Load start: ${url} (registration took ${timeline.phases.registration.toFixed(2)}ms)`, 'MaterialDebug');
        }
    }

    /**
     * Progress Event verzeichnen
     */
    public recordProgress(url: string, loaded: number, total: number): void {
        if (!this.isEnabled) return;
        
        const timeline = this.timelines.get(url);
        if (timeline) {
            const now = performance.now();
            const percent = total > 0 ? (loaded / total) * 100 : 0;
            
            timeline.progressEvents.push({
                timestamp: now,
                loaded,
                total,
                percent
            });

            if (!timeline.timestamps.firstProgress) {
                timeline.timestamps.firstProgress = now;
                //Logger.Instance.Debug(`📈 [DEBUG] First progress: ${url} - ${percent.toFixed(1)}% (${loaded}/${total} bytes)`, 'MaterialDebug');
            }
            
            timeline.timestamps.lastProgress = now;
            timeline.fileSize = total;

            // Log signifikante Progress-Meilensteine
            if (percent >= 25 && percent < 26) {
                //Logger.Instance.Debug(`📈 [DEBUG] 25% loaded: ${url} - ${(now - timeline.timestamps.loadStart).toFixed(2)}ms`, 'MaterialDebug');
            } else if (percent >= 50 && percent < 51) {
                //Logger.Instance.Debug(`📈 [DEBUG] 50% loaded: ${url} - ${(now - timeline.timestamps.loadStart).toFixed(2)}ms`, 'MaterialDebug');
            } else if (percent >= 75 && percent < 76) {
                //Logger.Instance.Debug(`📈 [DEBUG] 75% loaded: ${url} - ${(now - timeline.timestamps.loadStart).toFixed(2)}ms`, 'MaterialDebug');
            }
        }
    }

    /**
     * Loading abgeschlossen
     */
    public markCompleted(url: string, dimensions?: { width: number; height: number }): void {
        if (!this.isEnabled) return;
        
        const timeline = this.timelines.get(url);
        if (timeline) {
            const now = performance.now();
            timeline.timestamps.completed = now;
            timeline.totalTime = now - timeline.timestamps.registered;
            timeline.dimensions = dimensions;
            
            // Phasen berechnen
            if (timeline.timestamps.loadStart > 0) {
                timeline.phases.actualLoading = now - timeline.timestamps.loadStart;
            }
            
            // Processing time (falls verfügbar)
            if (timeline.timestamps.lastProgress) {
                timeline.phases.processing = now - timeline.timestamps.lastProgress;
            }

            this.logCompletionSummary(timeline);
        }
    }

    /**
     * Error verzeichnen
     */
    public markError(url: string, error: string): void {
        if (!this.isEnabled) return;
        
        const timeline = this.timelines.get(url);
        if (timeline) {
            timeline.timestamps.error = performance.now();
            Logger.Instance.Error(`❌ [DEBUG] Loading failed: ${url} - ${error}`, 'MaterialDebug');
        }
    }

    /**
     * Detaillierte Completion Summary
     */
    private logCompletionSummary(timeline: LoadingTimeline): void {
        const totalTime = timeline.totalTime;
        const reg = timeline.phases.registration;
        const loading = timeline.phases.actualLoading;
        const processing = timeline.phases.processing;
        
        //Logger.Instance.Info(
            //`✅ [DEBUG] COMPLETED: ${timeline.url}\n` +
            //`   📊 Total: ${totalTime.toFixed(2)}ms\n` +
            //`   📋 Registration: ${reg.toFixed(2)}ms (${(reg/totalTime*100).toFixed(1)}%)\n` +
            //`   📡 Loading: ${loading.toFixed(2)}ms (${(loading/totalTime*100).toFixed(1)}%)\n` +
            //`   ⚙️  Processing: ${processing.toFixed(2)}ms (${(processing/totalTime*100).toFixed(1)}%)\n` +
            //`   📦 Size: ${timeline.fileSize ? (timeline.fileSize/1024).toFixed(1) + 'KB' : 'unknown'}\n` +
            //`   📐 Dimensions: ${timeline.dimensions ? `${timeline.dimensions.width}x${timeline.dimensions.height}` : 'unknown'}\n` +
            //`   📈 Progress Events: ${timeline.progressEvents.length}`,
            //'MaterialDebug'
        //);
    }

    /**
     * Globale Performance-Analyse
     */
    public generatePerformanceReport(): void {
        if (!this.isEnabled) return;
        
        const completed = Array.from(this.timelines.values())
            .filter(t => t.timestamps.completed > 0);
        
        if (completed.length === 0) {
            //Logger.Instance.Info('📊 [DEBUG] No completed materials to analyze', 'MaterialDebug');
            return;
        }

        // Statistiken berechnen
        const textures = completed.filter(t => t.type === 'texture');
        const videos = completed.filter(t => t.type === 'video');
        
        const avgTexture = textures.length > 0 ? 
            textures.reduce((sum, t) => sum + t.totalTime, 0) / textures.length : 0;
        const avgVideo = videos.length > 0 ? 
            videos.reduce((sum, t) => sum + t.totalTime, 0) / videos.length : 0;
        
        const slowestTexture = textures.length > 0 ? 
            textures.reduce((max, t) => t.totalTime > max.totalTime ? t : max) : null;
        const slowestVideo = videos.length > 0 ? 
            videos.reduce((max, t) => t.totalTime > max.totalTime ? t : max) : null;

        //Logger.Instance.Info(
        //    `📊 [DEBUG] PERFORMANCE REPORT\n` +
        //    `   🖼️  Textures: ${textures.length} (avg: ${avgTexture.toFixed(2)}ms)\n` +
        //    `   🎬 Videos: ${videos.length} (avg: ${avgVideo.toFixed(2)}ms)\n` +
        //    `   🐌 Slowest Texture: ${slowestTexture ? `${slowestTexture.url} (${slowestTexture.totalTime.toFixed(2)}ms)` : 'none'}\n` +
        //    `   🐌 Slowest Video: ${slowestVideo ? `${slowestVideo.url} (${slowestVideo.totalTime.toFixed(2)}ms)` : 'none'}\n` +
        //    `   ⏱️  Total Session Time: ${(performance.now() - this.globalStartTime).toFixed(2)}ms`,
        //    'MaterialDebug'
        //);

        // Detaillierte Analyse der langsamsten Items
        if (slowestTexture && slowestTexture.totalTime > 2000) {
            this.analyzeSlowItem(slowestTexture);
        }
        if (slowestVideo && slowestVideo.totalTime > 2000) {
            this.analyzeSlowItem(slowestVideo);
        }
    }

    /**
     * Detaillierte Analyse eines langsamen Items
     */
    private analyzeSlowItem(timeline: LoadingTimeline): void {
        Logger.Instance.Warn(
            `🔍 [DEBUG] SLOW ITEM ANALYSIS: ${timeline.url}\n` +
            `   Type: ${timeline.type}\n` +
            `   Total Time: ${timeline.totalTime.toFixed(2)}ms\n` +
            `   Registration Delay: ${timeline.phases.registration.toFixed(2)}ms\n` +
            `   Network Loading: ${timeline.phases.actualLoading.toFixed(2)}ms\n` +
            `   Processing Time: ${timeline.phases.processing.toFixed(2)}ms\n` +
            `   File Size: ${timeline.fileSize ? (timeline.fileSize/1024).toFixed(1) + 'KB' : 'unknown'}\n` +
            `   Progress Events: ${timeline.progressEvents.length}\n` +
            `   Download Speed: ${this.calculateDownloadSpeed(timeline)} KB/s`,
            'MaterialDebug'
        );
    }

    /**
     * Download-Geschwindigkeit berechnen
     */
    private calculateDownloadSpeed(timeline: LoadingTimeline): string {
        if (!timeline.fileSize || timeline.phases.actualLoading === 0) {
            return 'unknown';
        }
        
        const sizeKB = timeline.fileSize / 1024;
        const timeSeconds = timeline.phases.actualLoading / 1000;
        const speedKBps = sizeKB / timeSeconds;
        
        return speedKBps.toFixed(1);
    }

    /**
     * Debugging ein-/ausschalten
     */
    public setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
        if (enabled) {
            //Logger.Instance.Info('🔧 [DEBUG] Material loading debugging enabled', 'MaterialDebug');
        } else {
            //Logger.Instance.Info('🔧 [DEBUG] Material loading debugging disabled', 'MaterialDebug');
        }
    }

    /**
     * Timeline für spezifische URL abrufen
     */
    public getTimeline(url: string): LoadingTimeline | undefined {
        return this.timelines.get(url);
    }

    /**
     * Alle Timelines abrufen
     */
    public getAllTimelines(): LoadingTimeline[] {
        return Array.from(this.timelines.values());
    }

    /**
     * Reset für neues Debugging
     */
    public reset(): void {
        this.timelines.clear();
        this.globalStartTime = 0;
        //Logger.Instance.Info('🔄 [DEBUG] Material loading debugger reset', 'MaterialDebug');
    }

    /**
     * Globale Debug-Funktion für Browser Console
     */
    public static setupGlobalDebugFunctions(): void {
        // Mache Debug-Funktionen global verfügbar
        (window as any).debugMaterialLoading = {
            report: () => MaterialLoadingDebugger.Instance.generatePerformanceReport(),
            timelines: () => MaterialLoadingDebugger.Instance.getAllTimelines(),
            getTimeline: (url: string) => MaterialLoadingDebugger.Instance.getTimeline(url),
            reset: () => MaterialLoadingDebugger.Instance.reset(),
            enable: () => MaterialLoadingDebugger.Instance.setEnabled(true),
            disable: () => MaterialLoadingDebugger.Instance.setEnabled(false),
            analyzeSlowMaterials: () => {
                const timelines = MaterialLoadingDebugger.Instance.getAllTimelines()
                    .filter(t => t.timestamps.completed > 0)
                    .sort((a, b) => b.totalTime - a.totalTime);
                
                console.log('🐌 SLOWEST MATERIALS:', timelines.slice(0, 5));
                return timelines;
            },
            summary: () => {
                const all = MaterialLoadingDebugger.Instance.getAllTimelines();
                const completed = all.filter(t => t.timestamps.completed > 0);
                const textures = completed.filter(t => t.type === 'texture');
                const videos = completed.filter(t => t.type === 'video');
                
                //console.log(`📊 SUMMARY:
                //    Total: ${all.length}
                //    Completed: ${completed.length}
                //    Textures: ${textures.length} (avg: ${textures.length > 0 ? (textures.reduce((sum, t) => sum + t.totalTime, 0) / textures.length).toFixed(2) : 0}ms)
                //    Videos: ${videos.length} (avg: ${videos.length > 0 ? (videos.reduce((sum, t) => sum + t.totalTime, 0) / videos.length).toFixed(2) : 0}ms)`);
                
                return { all, completed, textures, videos };
            }
        };
        
        //console.log(`🔧 Material Loading Debug Functions available:
        //    - debugMaterialLoading.report() - Generate performance report
        //    - debugMaterialLoading.summary() - Quick summary
        //    - debugMaterialLoading.timelines() - Get all timelines
        //    - debugMaterialLoading.analyzeSlowMaterials() - Find slowest materials
        //    - debugMaterialLoading.getTimeline(url) - Get specific timeline
        //    - debugMaterialLoading.reset() - Reset debugger
        //    - debugMaterialLoading.enable/disable() - Toggle debugging`);
    }
} 